// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Platform surface ids, tags and collections ─────────────────────────────
export const CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES = {
    /** Slice A/B — the catalogue: criteria, objectives, content items, Mức values, participant kinds. */
    catalogue: 'cybersecurity_assurance_index_catalogue',
    /** Slice A/D — the assessment period: role, systems and level, members, confidentiality. */
    period: 'cybersecurity_assurance_index_period',
    /** Slice C — one direction-and-management statistic with its components. */
    statistic: 'cybersecurity_assurance_index_statistic',
    /** Slice E — an evidence request to a supplier. */
    supplierRequest: 'cybersecurity_assurance_index_supplier_request',
};
/** The guidance tag every page of these registers renders through its boundary section. */
export const CYBERSECURITY_ASSURANCE_INDEX_BOUNDARY_TAG = 'cybersecurity_assurance_index_boundary';
export const CYBERSECURITY_ASSURANCE_INDEX_PERIODS_COLLECTION = 'grc_cybersecurity_assurance_index_periods';
export const CYBERSECURITY_ASSURANCE_INDEX_ENTRIES_COLLECTION = 'grc_cybersecurity_assurance_index_entries';
export const CYBERSECURITY_ASSURANCE_INDEX_STATISTICS_COLLECTION = 'grc_cybersecurity_assurance_index_statistics';
export const CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_REQUESTS_COLLECTION = 'grc_cybersecurity_assurance_index_supplier_requests';
/** The same names, keyed by record family. */
export const CYBERSECURITY_ASSURANCE_INDEX_COLLECTIONS = {
    periods: CYBERSECURITY_ASSURANCE_INDEX_PERIODS_COLLECTION,
    entries: CYBERSECURITY_ASSURANCE_INDEX_ENTRIES_COLLECTION,
    statistics: CYBERSECURITY_ASSURANCE_INDEX_STATISTICS_COLLECTION,
    supplierRequests: CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_REQUESTS_COLLECTION,
};
/**
 * The template field ids the registers read options from. They are the
 * record's own field ids (B0 attribute contract), never a value.
 */
export const CYBERSECURITY_ASSURANCE_INDEX_FIELDS = {
    catalogue: {
        criterion: 'criterion_code',
        objective: 'objective_code',
        content: 'content_code',
        rating: 'rating',
        participantKind: 'participant_kind',
        // D4 as revised: the evidence-date statement checkboxes are found by the
        // pack's `evidence_date_statement` attribute only (Remediation W, pack
        // 1.2.1); no field id names them here.
    },
    period: {
        assessorRole: 'assessor_role',
        systemLevel: 'system_level',
        members: 'members',
        containsStateSecrets: 'contains_state_secrets',
        /** The owner's assessment: the capacity the organisation takes part in. */
        ownerParticipantKind: 'participant_kind',
    },
    statistic: {
        code: 'criterion_code',
        unit: 'unit',
        authorityFigure: 'authority_figure',
    },
    supplierRequest: {
        evidenceKind: 'requested_evidence_kind',
    },
};
/**
 * Pack attribute names the registers read off an option (or a field). Platform
 * vocabulary only: each is a KEY a pack may author. A flag the pack did not
 * author reads false; a value it did not author reads null — never a guess.
 */
export const CYBERSECURITY_ASSURANCE_INDEX_PACK_ATTR = {
    /** Criterion option: the group the criterion belongs to (pack text, shown, never compared). */
    group: 'group',
    /**
     * Criterion option: the group's display name, EN/VI, as the pack authors it
     * (E12.5 p1, BE-14). The registers print a group by this label and never
     * add a word of their own to the group code; where the pack authors none,
     * the group prints as authored.
     */
    groupLabel: 'group_label',
    /** Criterion / statistic option: the installed requirement the item rests on (its evidence marker). */
    requirementId: 'requirement_id',
    /** Criterion option: `rated_content` or `statistic`. */
    entryKind: 'entry_kind',
    /** Criterion / objective / content option: the `system_level` option value the item applies from. */
    appliesFromLevel: 'applies_from_level',
    /** Criterion option: offered only to a system whose software development is recorded. */
    appliesOnlyWithSoftwareDevelopment: 'applies_only_with_software_development',
    /** Objective / content option: the criterion code it belongs to. */
    criterionCode: 'criterion_code',
    /** Content option: the objective code it belongs to. */
    objectiveCode: 'objective_code',
    /** Content option: the `rating` option values this item defines (D3). */
    definedRatings: 'defined_ratings',
    /** Content option: the evidence cell, EN/VI. */
    evidence: 'evidence',
    /** Content option: per rating value, what that Mức means for this item, EN/VI (OQ-1). */
    ratingConditions: 'rating_conditions',
    /** Content option: the page of the Decision the item is printed on. */
    sourcePage: 'source_page',
    /** Criterion / objective / content option: the level condition printed on that row, EN/VI (shown, never compared). */
    levelQualifier: 'level_qualifier',
    /** Catalogue checkbox FIELD: the side of the period an evidence-date statement covers (D4). */
    evidenceDateStatement: 'evidence_date_statement',
    /** Content option: the Mức text varies by level (shown, never encoded). */
    levelDependentTextInRatings: 'level_dependent_text_in_ratings',
    /** Rating option: a Mức that needs an explanation and at least one piece of evidence of the period (AC5). */
    requiresExplanationAndEvidence: 'requires_explanation_and_evidence',
    /** Participant-kind option: a certification against another standard may be recorded as the basis (AC7). */
    certificationBasisAllowed: 'certification_basis_allowed',
    /** Option-level: the requirement ids whose applicability decides whether the option is offered. */
    appliesToRequirementIds: 'applies_to_requirement_ids',
    /** `system_level` FIELD attribute: the classification schemes whose approved tier is the level (CL-2). */
    readsClassificationSchemeIds: 'reads_classification_scheme_ids',
    /** Level option: its rank; absent on a level with no rank (`not approved`). */
    rank: 'rank',
    /** Level option: the classification tier codes it corresponds to (CL-2). */
    classificationTierCodes: 'classification_tier_codes',
    /** `contains_state_secrets` option: every page of the period shows the confidentiality guidance (AC12). */
    showsConfidentialityGuidance: 'shows_confidentiality_guidance',
    /** `contains_state_secrets` option: the guidance record that confidentiality guidance is. */
    guidanceId: 'guidance_id',
    /** `assessor_role` option: the period records the owner's assessment it takes part in. */
    requiresOwnerAssessmentDetails: 'requires_owner_assessment_details',
    /** Statistic code option: the formula has a denominator (AC8). */
    hasDenominator: 'has_denominator',
    /** Statistic code option: the `unit` option value the statistic is stated in. */
    unit: 'unit',
    /** Statistic code option: the authority-figure floor applies (AC9). */
    authorityFigureFloor: 'authority_figure_floor',
    /** Statistic code option: the component the authority's figure is compared to (`numerator` / `denominator`). */
    authorityFigureComponent: 'authority_figure_compares_to',
    /** Statistic code option: the formula, EN/VI, the label of the value derived on read. */
    formula: 'formula',
    /** Statistic code option: what the numerator counts, EN/VI. */
    numeratorLabel: 'numerator_label',
    /** Statistic code option: what the denominator counts, EN/VI. */
    denominatorLabel: 'denominator_label',
    /** Statistic code option: the direction the pack states (pack text, never compared). */
    statisticDirection: 'statistic_direction',
    /** Statistic code option: the basis-of-figures sentence the floor refusal quotes, EN/VI. */
    figureBasis: 'figure_basis',
    /** Supplier-request evidence-kind option: the content codes the kind serves. */
    contentCodes: 'content_codes',
    /** Supplier-request evidence-kind option: the kind serves any item (no `content_codes` restriction). */
    appliesToAnyContent: 'applies_to_any_content',
};
/** A catalogue criterion's kind (platform vocabulary of the attribute contract). */
export const CYBERSECURITY_ASSURANCE_INDEX_ENTRY_KINDS = ['rated_content', 'statistic'];
/** A period is open while entries are recorded; closed, it is bound to its snapshot (AC18). */
export const CYBERSECURITY_ASSURANCE_INDEX_PERIOD_STATUSES = ['open', 'closed'];
/**
 * The recording state of one offered item (OQ-3). What progress may count:
 * never a count or share per Mức and never an average.
 */
export const CYBERSECURITY_ASSURANCE_INDEX_RECORDING_STATES = [
    'recorded',
    'awaiting',
    'excluded_with_evidence',
    'exclusion_not_evidenced',
];
/** Why an item is or is not offered for a system (AC4). */
export const CYBERSECURITY_ASSURANCE_INDEX_OFFER_REASONS = [
    'offered',
    'below_level',
    'level_not_determinable',
    'software_development_not_recorded',
    'qualifier_unreadable',
];
/** Where a system's level came from (CL-2). */
export const CYBERSECURITY_ASSURANCE_INDEX_LEVEL_SOURCES = [
    'classification_profile',
    'entered',
];
/** Which component of a statistic the competent authority's figure counts (AC9). */
export const CYBERSECURITY_ASSURANCE_INDEX_FIGURE_COMPONENTS = [
    'numerator',
    'denominator',
];
/**
 * D4 as revised by the legal reader (2026-10-27) — the platform's reading,
 * pending the standing body's guidance, never the Decision's rule: evidence
 * dated outside the period counts only with the person's statement for its
 * side of the period. Each statement is a checkbox field the catalogue template
 * marks with `evidence_date_statement` set to one of these kinds; its field id
 * is read from the pack, never a literal.
 */
export const CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_DATE_STATEMENT_KINDS = [
    /** Evidence dated before the period's start, stated to have been in force during it. */
    'before_period_start',
    /** Evidence dated after the period's end, stated to record facts or activity within it. */
    'after_period_end',
];
/** Why an evidence date is refused (D4 as revised): the side of the period and the missing statement. */
export const CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_DATE_REASONS = [
    'before_period_start_without_statement',
    'after_period_end_without_statement',
];
/** How a viewer reaches a period (OQ-2): named on it, or the dedicated read grant. */
export const CYBERSECURITY_ASSURANCE_INDEX_VIEWER_ACCESS = ['member', 'restricted_read'];
/** A supplier request's state, derived on read. */
export const CYBERSECURITY_ASSURANCE_INDEX_REQUEST_STATES = ['outstanding', 'replied'];
/** The locales an export is written in: the pack's own EN and VI. */
export const CYBERSECURITY_ASSURANCE_INDEX_EXPORT_LOCALES = ['en', 'vi'];
/** The most members, systems, evidence links and participants one record holds. */
export const CYBERSECURITY_ASSURANCE_INDEX_LIMITS = {
    members: 100,
    systems: 200,
    evidenceLinks: 50,
    participants: 20,
    contentCodesPerRequest: 100,
};
// ── Error codes (always the full prefix) ───────────────────────────────────
export const CYBERSECURITY_ASSURANCE_INDEX_ERROR_CODES = {
    /** 404 — the one not-found: identical for a denied read and an unknown id (OQ-2). */
    recordNotFound: 'CYBERSECURITY_ASSURANCE_INDEX_RECORD_NOT_FOUND',
    /** 409 — no operational install declares the surface (reference mode included, AC17). */
    registerNotDeclared: 'CYBERSECURITY_ASSURANCE_INDEX_REGISTER_NOT_DECLARED',
    /** 400 — a value is not an option the installed template declares. */
    optionNotDeclared: 'CYBERSECURITY_ASSURANCE_INDEX_OPTION_NOT_DECLARED',
    /** 422 — an option whose named requirements are out of scope (the pilot body, D1). */
    optionNotOffered: 'CYBERSECURITY_ASSURANCE_INDEX_OPTION_NOT_OFFERED',
    versionConflict: 'CYBERSECURITY_ASSURANCE_INDEX_RECORD_VERSION_CONFLICT',
    userNotFound: 'CYBERSECURITY_ASSURANCE_INDEX_USER_NOT_FOUND',
    dateOrderInvalid: 'CYBERSECURITY_ASSURANCE_INDEX_DATE_ORDER_INVALID',
    /** 409 — the period is closed and bound to its snapshot (AC18). */
    periodClosed: 'CYBERSECURITY_ASSURANCE_INDEX_PERIOD_CLOSED',
    /** 422 — the period already holds `CYBERSECURITY_ASSURANCE_INDEX_LIMITS.systems` systems. */
    systemsLimitReached: 'CYBERSECURITY_ASSURANCE_INDEX_SYSTEMS_LIMIT_REACHED',
    /** 404 — a system entry not on this period. */
    systemNotFound: 'CYBERSECURITY_ASSURANCE_INDEX_SYSTEM_NOT_FOUND',
    /** 404 — a linked classification profile or vendor that is not this organisation's. */
    linkedRecordNotFound: 'CYBERSECURITY_ASSURANCE_INDEX_LINKED_RECORD_NOT_FOUND',
    /** 422 — approved classifications exist under more than one named scheme: the person chooses. */
    classificationSchemeChoiceRequired: 'CYBERSECURITY_ASSURANCE_INDEX_CLASSIFICATION_SCHEME_CHOICE_REQUIRED',
    /** 422 — the approved tier corresponds to no declared level option. */
    classificationTierNotMapped: 'CYBERSECURITY_ASSURANCE_INDEX_CLASSIFICATION_TIER_NOT_MAPPED',
    /** 422 — a level entered where an approved classification is read. */
    levelReadFromClassification: 'CYBERSECURITY_ASSURANCE_INDEX_LEVEL_READ_FROM_CLASSIFICATION',
    /** 422 — an entered level without its approval reference, or a system without its name. */
    levelFieldsRequired: 'CYBERSECURITY_ASSURANCE_INDEX_LEVEL_FIELDS_REQUIRED',
    /** 422 — the system's level reading is not from a classification profile. */
    levelNotFromClassification: 'CYBERSECURITY_ASSURANCE_INDEX_LEVEL_NOT_FROM_CLASSIFICATION',
    /** 404 — a content code not in the period's catalogue. */
    itemNotInCatalogue: 'CYBERSECURITY_ASSURANCE_INDEX_ITEM_NOT_IN_CATALOGUE',
    /** 422 — the item is not offered for the system (AC4). `details.reason`. */
    itemNotOffered: 'CYBERSECURITY_ASSURANCE_INDEX_ITEM_NOT_OFFERED',
    /** 422 — a Mức the item does not define (D3). */
    ratingNotDefinedForItem: 'CYBERSECURITY_ASSURANCE_INDEX_RATING_NOT_DEFINED_FOR_ITEM',
    /** 422 — a Mức that needs an explanation and at least one piece of evidence of the period, without them (AC5). */
    ratingNeedsExplanationAndEvidence: 'CYBERSECURITY_ASSURANCE_INDEX_RATING_NEEDS_EXPLANATION_AND_EVIDENCE',
    /** 422 — an exclusion recorded together with a Mức. */
    exclusionWithRating: 'CYBERSECURITY_ASSURANCE_INDEX_EXCLUSION_WITH_RATING',
    /** 422 — evidence dated outside the period (D4). `details.reason`. */
    evidenceDateOutsidePeriod: 'CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_DATE_OUTSIDE_PERIOD',
    /** 422 — a file on a record resting on a restricted-evidence requirement (D2). */
    restrictedEvidenceNoFile: 'CYBERSECURITY_ASSURANCE_INDEX_RESTRICTED_EVIDENCE_NO_FILE',
    /** 422 — a file not held for this period under the evidence owner type. */
    attachmentNotOwned: 'CYBERSECURITY_ASSURANCE_INDEX_ATTACHMENT_NOT_OWNED',
    /** 422 — a certification basis on a participant kind that does not allow one (AC7). */
    certificationBasisNotAllowed: 'CYBERSECURITY_ASSURANCE_INDEX_CERTIFICATION_BASIS_NOT_ALLOWED',
    /** 422 — a statistic missing a required component, or with one its code does not have (AC8). */
    statisticComponentInvalid: 'CYBERSECURITY_ASSURANCE_INDEX_STATISTIC_COMPONENT_INVALID',
    /** 422 — a unit other than the code's declared unit. */
    unitNotForCode: 'CYBERSECURITY_ASSURANCE_INDEX_UNIT_NOT_FOR_CODE',
    /** 422 — a figure below the competent authority's, where the floor applies (AC9). */
    belowAuthorityFigure: 'CYBERSECURITY_ASSURANCE_INDEX_BELOW_AUTHORITY_FIGURE',
    /** 409 — the code is already recorded for this scope in this period. */
    statisticExists: 'CYBERSECURITY_ASSURANCE_INDEX_STATISTIC_EXISTS',
    /** 409 — a code recorded per period and per system in one period (D9). */
    statisticScopeMixed: 'CYBERSECURITY_ASSURANCE_INDEX_STATISTIC_SCOPE_MIXED',
    /**
     * 422 — a requested evidence kind that does not serve every item chosen
     * (Remediation W, legal): `details.not_served` names the chosen codes it
     * does not serve.
     */
    evidenceKindNotForItem: 'CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_KIND_NOT_FOR_ITEM',
    /**
     * 422 — Remediation W (U18): an `entered_by` or a `judged_by` who is not
     * named on the period (nor the caller), so could not open the record that
     * names them.
     */
    personNotAMember: 'CYBERSECURITY_ASSURANCE_INDEX_PERSON_NOT_A_MEMBER',
    /** 422 — Remediation W (U12): a cited supplier request that does not name the entry's item. */
    supplierRequestNotForItem: 'CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_REQUEST_NOT_FOR_ITEM',
    /** 422 — Remediation W (U12): a cited evidence entry the supplier request does not hold. */
    supplierEvidenceNotFound: 'CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_EVIDENCE_NOT_FOUND',
    /** 422 — Remediation W (U12): a citing link whose date or file is not the returned evidence's own. */
    supplierEvidenceNotAsReturned: 'CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_EVIDENCE_NOT_AS_RETURNED',
    /** 422 — returned evidence without the reply date. */
    replyFieldsRequired: 'CYBERSECURITY_ASSURANCE_INDEX_REPLY_FIELDS_REQUIRED',
    /** 422 — the owner's assessment recorded on a period whose role does not take part in one. */
    ownerAssessmentNotForRole: 'CYBERSECURITY_ASSURANCE_INDEX_OWNER_ASSESSMENT_NOT_FOR_ROLE',
    /** 422 — a participant's period without the owner-assessment fields the template requires. */
    ownerAssessmentFieldsRequired: 'CYBERSECURITY_ASSURANCE_INDEX_OWNER_ASSESSMENT_FIELDS_REQUIRED',
    /** 422 — a field the installed template requires, left empty. */
    fieldRequired: 'CYBERSECURITY_ASSURANCE_INDEX_FIELD_REQUIRED',
    /** 422 — a member list that would leave the period with none. */
    membersRequired: 'CYBERSECURITY_ASSURANCE_INDEX_MEMBERS_REQUIRED',
    /** 422 — an export larger than the module's bound. */
    exportTooLarge: 'CYBERSECURITY_ASSURANCE_INDEX_EXPORT_TOO_LARGE',
    /**
     * 422 — E12.5 p1 (BE-1): new period dates would leave evidence already
     * recorded on the period's entries (not excluded) or statistics outside the
     * period without the statement for its side (D4). `details.records` lists
     * each such record by id and code only (OQ-2): `{ record_type: 'entry' |
     * 'statistic', record_id, system_entry_id, content_code | criterion_code,
     * evidence_indexes, reasons }`; `details.field_errors` names the date fields sent.
     */
    periodDatesExcludeEvidence: 'CYBERSECURITY_ASSURANCE_INDEX_PERIOD_DATES_EXCLUDE_EVIDENCE',
    /**
     * 409 — E12.5 p1 (BE-5): a supplier-request change that would alter,
     * remove or reorder returned evidence an entry cites, drop an item a citing
     * entry records, or clear the reply. `details.citing_entry_ids` names the
     * citing entries; `details.field_errors` the fields refused.
     */
    supplierRequestCited: 'CYBERSECURITY_ASSURANCE_INDEX_SUPPLIER_REQUEST_CITED',
    /**
     * 422 — E12.5 p1 (QA-7): a system picked from a classification profile by a
     * caller who may not read the profile's name, without a `system_name`.
     */
    systemNameRequired: 'CYBERSECURITY_ASSURANCE_INDEX_SYSTEM_NAME_REQUIRED',
    /**
     * 409 — E12.5 p2: another change to the same assessment period holds its
     * write lease (an entry, a statistic, a supplier request, an evidence
     * upload, or a change to the period itself) and did not finish within the
     * brief wait; nothing was changed. Try again. `details`: `{ retry_after_ms }`;
     * when this write's own lease was lost to a stall past its expiry,
     * additionally `{ lease_lost: true, undone: boolean }` (`undone: false`
     * only where putting the record back would resurrect content the period's
     * state now excludes — the record then stands as written).
     */
    periodChangeInProgress: 'CYBERSECURITY_ASSURANCE_INDEX_PERIOD_CHANGE_IN_PROGRESS',
};
/** The most item rows one export carries (422 above it). */
export const CYBERSECURITY_ASSURANCE_INDEX_EXPORT_MAX_ROWS = 20000;
//# sourceMappingURL=cybersecurity-assurance-index.js.map